import { onBeforeUnmount, watch } from 'vue';
import type { IframeRect } from './types';

const CONTAINER_SELECTOR = '.router-tab__container';

function rectsAreEqual(
  previousRect: IframeRect | null,
  nextRect: IframeRect | null,
): boolean {
  if (previousRect === nextRect) {
    return true;
  }
  if (!previousRect || !nextRect) {
    return false;
  }
  return (
    previousRect.top === nextRect.top &&
    previousRect.left === nextRect.left &&
    previousRect.width === nextRect.width &&
    previousRect.height === nextRect.height
  );
}

export function useRectTracker(
  getTarget: () => HTMLElement | null,
  isActive: () => boolean,
  onChange: (rect: IframeRect | null) => void,
) {
  let lastReportedRect: IframeRect | null = null;
  let pendingMeasureFrame = 0;
  let placeholderResizeObserver: ResizeObserver | null = null;
  let containerResizeObserver: ResizeObserver | null = null;
  let contentMutationObserver: MutationObserver | null = null;

  function measurePlaceholder(): IframeRect | null {
    const placeholder = getTarget();
    if (!placeholder) {
      return null;
    }
    const container = placeholder.closest(CONTAINER_SELECTOR);
    if (!container) {
      return null;
    }
    const placeholderBox = placeholder.getBoundingClientRect();
    const containerBox = container.getBoundingClientRect();
    return {
      top: placeholderBox.top - containerBox.top,
      left: placeholderBox.left - containerBox.left,
      width: placeholderBox.width,
      height: placeholderBox.height,
    };
  }

  function measureAndReport() {
    pendingMeasureFrame = 0;
    const nextRect = measurePlaceholder();
    if (!rectsAreEqual(lastReportedRect, nextRect)) {
      lastReportedRect = nextRect;
      onChange(nextRect);
    }
  }

  function scheduleMeasure() {
    if (pendingMeasureFrame) {
      return;
    }
    pendingMeasureFrame = requestAnimationFrame(measureAndReport);
  }

  function start() {
    const placeholder = getTarget();
    const container = placeholder?.closest(CONTAINER_SELECTOR);

    if (typeof ResizeObserver !== 'undefined') {
      placeholderResizeObserver = new ResizeObserver(scheduleMeasure);
      if (placeholder) {
        placeholderResizeObserver.observe(placeholder);
      }
      containerResizeObserver = new ResizeObserver(scheduleMeasure);
      if (container) {
        containerResizeObserver.observe(container);
      }
    }

    if (typeof MutationObserver !== 'undefined' && container) {
      contentMutationObserver = new MutationObserver(scheduleMeasure);
      contentMutationObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener('scroll', scheduleMeasure, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', scheduleMeasure, { passive: true });

    measureAndReport();
  }

  function stop() {
    if (pendingMeasureFrame) {
      cancelAnimationFrame(pendingMeasureFrame);
      pendingMeasureFrame = 0;
    }
    placeholderResizeObserver?.disconnect();
    placeholderResizeObserver = null;
    containerResizeObserver?.disconnect();
    containerResizeObserver = null;
    contentMutationObserver?.disconnect();
    contentMutationObserver = null;
    window.removeEventListener('scroll', scheduleMeasure, { capture: true });
    window.removeEventListener('resize', scheduleMeasure);
    lastReportedRect = null;
  }

  watch(isActive, (active) => (active ? start() : stop()), { immediate: true });
  onBeforeUnmount(stop);
}
